// Client side of the Meta CAPI "EngagedVisit" event.
//
// A visitor counts as engaged on a page once they have >= 30s of ACTIVE time
// (tab visible AND an interaction/scroll within the last 15s) and have scrolled
// >= 50% of it. The tracker fires `onEngaged` once per path; the SPA does not
// reload on navigation, so `navigate()` resets the counters per route.
//
// `createEngagementTracker` is pure (clock and callback injected) so it is unit
// tested without a DOM. `startEngagementTracking` wires it to the browser and
// POSTs a beacon to /api/meta/engaged, which re-checks the thresholds and
// forwards to Meta (apps/api/src/meta/engaged-visit.ts).

import { getHeroVariant } from "@/lib/lifetime-hero";

export const MIN_ACTIVE_SECONDS = 30;
export const MIN_SCROLL_FRACTION = 0.5;
/** An interaction keeps the clock running for this long. */
export const IDLE_AFTER_MS = 15_000;
/** Single tick is credited at most this much, so a throttled timer can't inflate time. */
const MAX_TICK_CREDIT_MS = 2_000;

/**
 * Public marketing routes only. Logged-in app routes are deliberately absent:
 * the event measures ad-landing interest, not product usage. /lifetime is the
 * current Sales-campaign landing page, "/" is the default site link.
 */
export const ENGAGED_VISIT_PATHS: readonly string[] = [
  "/",
  "/lifetime",
  "/pricing",
  "/how-it-works",
  "/security",
  "/case-studies/nexlytics",
];

export function isTrackedPath(pathname: string): boolean {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return ENGAGED_VISIT_PATHS.includes(clean);
}

export interface EngagedPayload {
  path: string;
  activeSeconds: number;
  maxScroll: number;
}

export interface EngagementTracker {
  /** Call on first load and on every SPA route change. Resets counters. */
  navigate(pathname: string): void;
  /** Any click/key/pointer/scroll/touch. */
  interaction(): void;
  setVisible(visible: boolean): void;
  /** Current scroll depth as a 0..1 fraction; only the maximum is kept. */
  scroll(fraction: number): void;
  /** Call about once a second. */
  tick(): void;
}

export function createEngagementTracker(opts: {
  now: () => number;
  onEngaged: (p: EngagedPayload) => void;
  isTracked?: (pathname: string) => boolean;
}): EngagementTracker {
  const isTracked = opts.isTracked ?? isTrackedPath;
  let path: string | null = null;
  let activeMs = 0;
  let maxScroll = 0;
  let lastInteraction = 0;
  let lastTick = 0;
  let visible = true;
  const fired = new Set<string>();

  function check() {
    if (!path || fired.has(path)) return;
    if (activeMs >= MIN_ACTIVE_SECONDS * 1000 && maxScroll >= MIN_SCROLL_FRACTION) {
      fired.add(path);
      try {
        opts.onEngaged({
          path,
          activeSeconds: Math.floor(activeMs / 1000),
          maxScroll: Math.round(maxScroll * 100) / 100,
        });
      } catch {
        // Tracking must never break the page.
      }
    }
  }

  return {
    navigate(pathname) {
      const t = opts.now();
      path = isTracked(pathname) ? (pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname) : null;
      activeMs = 0;
      maxScroll = 0;
      lastInteraction = t;
      lastTick = t;
    },
    interaction() {
      lastInteraction = opts.now();
    },
    setVisible(v) {
      visible = v;
      lastTick = opts.now();
    },
    scroll(fraction) {
      if (!Number.isFinite(fraction)) return;
      lastInteraction = opts.now();
      maxScroll = Math.max(maxScroll, Math.min(1, Math.max(0, fraction)));
      check();
    },
    tick() {
      const t = opts.now();
      const delta = Math.min(Math.max(t - lastTick, 0), MAX_TICK_CREDIT_MS);
      lastTick = t;
      if (!path || !visible) return;
      if (t - lastInteraction > IDLE_AFTER_MS) return;
      activeMs += delta;
      check();
    },
  };
}

// ─── Browser wiring ──────────────────────────────────────────────────────────

const VISITOR_KEY = "mb_vid";

function readCookie(name: string): string {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : "";
}

/** Stable first-party visitor id (cookie, 1 year), the CAPI external_id source. */
export function getVisitorId(): string {
  try {
    const existing = readCookie(VISITOR_KEY);
    if (/^[A-Za-z0-9_-]{8,64}$/.test(existing)) return existing;
    const id = crypto.randomUUID();
    document.cookie = `${VISITOR_KEY}=${id}; Max-Age=31536000; Path=/; SameSite=Lax`;
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

/** _fbc cookie, else built from ?fbclid= per Meta's fb.1.<ms>.<fbclid> format. */
export function getFbc(search: string, nowMs: number): string {
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;
  const fbclid = new URLSearchParams(search).get("fbclid");
  return fbclid ? `fb.1.${nowMs}.${fbclid}` : "";
}

let started = false;

/** Starts listeners once. Returns a function to call on each SPA route change. */
export function startEngagementTracking(apiUrl: (path: string) => string): (pathname: string) => void {
  if (started || typeof window === "undefined") return () => {};
  started = true;

  const sent = new Set<string>();
  const tracker = createEngagementTracker({
    now: () => Date.now(),
    onEngaged: (p) => {
      // Belt and braces with the tracker's own once-per-path guard: a reload
      // within the same tab session must not resend.
      const key = `mb_ev_${p.path}`;
      try {
        if (window.sessionStorage.getItem(key) === "1") return;
        window.sessionStorage.setItem(key, "1");
      } catch {
        if (sent.has(key)) return;
        sent.add(key);
      }
      const body = JSON.stringify({
        visitorId: getVisitorId(),
        path: p.path,
        activeSeconds: p.activeSeconds,
        maxScroll: p.maxScroll,
        heroVariant: p.path === "/lifetime" ? getHeroVariant() : undefined,
        fbp: readCookie("_fbp") || undefined,
        fbc: getFbc(window.location.search, Date.now()) || undefined,
      });
      const url = apiUrl("/api/meta/engaged");
      // text/plain keeps this a CORS "simple" request (no preflight), which
      // matters when the API host differs from the page host (staging).
      try {
        if (navigator.sendBeacon?.(url, new Blob([body], { type: "text/plain" }))) return;
        void fetch(url, { method: "POST", body, keepalive: true, headers: { "content-type": "text/plain" } }).catch(
          () => {},
        );
      } catch {
        // never surface tracking errors
      }
    },
  });

  const onInteraction = () => tracker.interaction();
  for (const ev of ["pointerdown", "keydown", "touchstart", "mousemove", "wheel"]) {
    window.addEventListener(ev, onInteraction, { passive: true });
  }
  window.addEventListener(
    "scroll",
    () => {
      const el = document.documentElement;
      const scrollable = el.scrollHeight - window.innerHeight;
      // A page that fits the viewport has nothing to scroll: count it as read.
      tracker.scroll(scrollable <= 0 ? 1 : (window.scrollY + window.innerHeight) / el.scrollHeight);
    },
    { passive: true },
  );
  document.addEventListener("visibilitychange", () => tracker.setVisible(document.visibilityState === "visible"));
  setInterval(() => tracker.tick(), 1000);

  return (pathname: string) => {
    tracker.navigate(pathname);
    // Re-evaluate scroll depth for the new page (e.g. a short page).
    window.dispatchEvent(new Event("scroll"));
  };
}
